import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Search, Folder, FileText, Mic, Globe, Lock, X, Link as LinkIcon } from 'lucide-react';

const KINDS = [
  { id: 'all', label: 'Tutte' },
  { id: 'folder', label: 'Cartelle' },
  { id: 'content', label: 'Contenuti' },
  { id: 'phoneme', label: 'Fonemi' },
  { id: 'page', label: 'Pagine' },
];
const ICONS = { folder: Folder, content: FileText, phoneme: Mic, page: Globe };

export const ResourceIcon = ({ kind, className = 'w-4 h-4' }) => {
  const Icon = ICONS[kind] || LinkIcon;
  return <Icon className={className} />;
};

export const ResourcePicker = ({ backendUrl, token, value, onChange, testIdPrefix = 'rp' }) => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');

  useEffect(() => {
    axios.get(`${backendUrl}/api/admin/resources`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [backendUrl, token]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return items.filter((i) => (kind === 'all' || i.kind === kind)
      && (!s || `${i.title} ${i.subtitle}`.toLowerCase().includes(s)));
  }, [items, q, kind]);

  if (value) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30" data-testid={`${testIdPrefix}-selected`}>
        <ResourceIcon kind={value.kind} className="w-4 h-4 text-cyan-300" />
        <span className="flex-1 min-w-0">
          <span className="block text-sm text-white truncate">{value.title}</span>
          <span className="block text-xs text-slate-400 truncate">{value.link}</span>
        </span>
        <button type="button" onClick={() => onChange(null)} className="text-slate-400 hover:text-white" data-testid={`${testIdPrefix}-clear`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca una risorsa (cartella, video, PDF, fonema, pagina…)"
          className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm"
          data-testid={`${testIdPrefix}-search`}
        />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {KINDS.map((k) => (
          <button
            type="button" key={k.id} onClick={() => setKind(k.id)}
            className={`px-2.5 py-1 rounded-full text-xs transition-colors ${kind === k.id ? 'bg-cyan-500 text-slate-950 font-semibold' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
            data-testid={`${testIdPrefix}-kind-${k.id}`}
          >{k.label}</button>
        ))}
      </div>
      <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-700 divide-y divide-slate-800">
        {filtered.length === 0 && <p className="text-center text-sm text-slate-500 py-5">Nessuna risorsa</p>}
        {filtered.map((i) => (
          <button
            type="button" key={`${i.kind}-${i.id}`} onClick={() => onChange(i)}
            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-800 transition-colors"
            data-testid={`${testIdPrefix}-item-${i.kind}-${i.id}`}
          >
            <ResourceIcon kind={i.kind} className="w-4 h-4 text-cyan-400 flex-shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block text-sm text-white truncate">{i.title}</span>
              <span className="block text-xs text-slate-500 truncate">{i.subtitle}</span>
            </span>
            {!i.is_public && <Lock className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" title="Riservata" />}
          </button>
        ))}
      </div>
    </div>
  );
};

export const ResourceCard = ({ m, onOpen, testId }) => (
  <button
    type="button" onClick={onOpen}
    className="mt-2 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/15 text-left transition-colors"
    data-testid={testId}
  >
    <span className="w-8 h-8 rounded-lg bg-cyan-400/20 flex items-center justify-center flex-shrink-0">
      <ResourceIcon kind={m.resource_kind} className="w-4 h-4 text-cyan-200" />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-[10px] uppercase tracking-widest opacity-70">Risorsa</span>
      <span className="block text-sm font-semibold truncate">{m.resource_title}</span>
    </span>
    <span className="text-xs font-semibold opacity-90 flex-shrink-0">Apri →</span>
  </button>
);
